import { classNames } from "../../classNames";
import type { Column, ColumnAlign, ColumnType } from "./DataTableTypes";

/** Width in px of the column types whose content has a predictable size. */
const DEFAULT_WIDTH: Partial<Record<ColumnType, number>> = {
  date: 136,
  datetime: 148,
  badge: 140,
  number: 112,
  money: 144,
  actions: 64,
};

/** Width a flexible column without `minWidth` counts for in the table's minimum width. */
const FLEXIBLE_COLUMN_WIDTH = 160;

const RIGHT_ALIGNED_TYPES: readonly ColumnType[] = ["number", "money", "actions"];

export function columnAlign<R>(col: Column<R>): ColumnAlign {
  if (col.align) return col.align;
  return col.type !== undefined && RIGHT_ALIGNED_TYPES.includes(col.type) ? "right" : "left";
}

/** `is-right` / `is-center`; left alignment is the default and needs no class. */
export function alignClass<R>(col: Column<R>): string | undefined {
  const align = columnAlign(col);
  return align !== "left" ? `is-${align}` : undefined;
}

export function isSortable<R>(col: Column<R>): boolean {
  if (col.type === "actions") return false;
  return col.sortable ?? true;
}

/** Fixed width of the column: its own, or the default of its type. */
export function columnWidth<R>(col: Column<R>): number | undefined {
  return col.width ?? (col.type ? DEFAULT_WIDTH[col.type] : undefined);
}

/** Below this width the table scrolls horizontally instead of squeezing its columns. */
export function tableMinWidth<R>(columns: Column<R>[]): number {
  return columns.reduce((sum, col) => sum + (columnWidth(col) ?? col.minWidth ?? FLEXIBLE_COLUMN_WIDTH), 0);
}

/** Classes of a body cell: alignment, monospace, actions and wrapping. */
export function cellClassName<R>(col: Column<R>): string | undefined {
  return (
    classNames(alignClass(col), col.type === "mono" && "is-mono", col.type === "actions" && "is-actions", col.wrap && "is-wrap") ||
    undefined
  );
}
